import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Search,
  FileText,
  ShieldCheck,
  X,
  Upload,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Users,
  BookOpen,
  FileSearch,
  Network,
  LockKeyhole,
  ArrowDown,
} from "lucide-react";
import "./OperationalMemory.css";

const API = "/api/operational-memory";
const suggested = [
  "Why were these records accepted Use As Is?",
  "What did the weld inspection find?",
  "What cause records are missing?",
  "Who participated in the dispositions?",
];
async function request(path = "", options) {
  const r = await fetch(API + path, options);
  if (!r.ok) {
    let body = await r.json().catch(() => ({}));
    throw new Error(
      typeof body.detail === "string"
        ? body.detail
        : "Operational Memory could not be loaded. Please try again.",
    );
  }
  return r.json();
}
const Tag = ({ kind, children }) => (
  <span className={`om-tag ${kind || ""}`}>{children}</span>
);

export default function OperationalMemory() {
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [retry, setRetry] = useState(0),
    [evidence, setEvidence] = useState(null),
    [upload, setUpload] = useState(false);
  const { recordId } = useParams();
  const [params] = useSearchParams();
  const query = params.get("q") || "";
  const navigate = useNavigate();
  const location = useLocation();
  const isAsk = location.pathname === "/operational-memory/ask";
  useEffect(() => {
    const oldTitle = document.title;
    document.title = `${recordId || (isAsk ? "Ask Operational Memory" : "Operational Memory")} · Fluxonix`;
    window.scrollTo(0, 0);
    return () => {
      document.title = oldTitle;
    };
  }, [recordId, isAsk, query]);
  useEffect(() => {
    let active = true;
    setError("");
    request()
      .then((d) => active && setData(d))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [retry]);
  const goAsk = (q) =>
    navigate("/operational-memory/ask?q=" + encodeURIComponent(q));
  const record = data?.records.find((r) => r.id === recordId);
  const name = (id) => data?.nodes.find((n) => n.id === id)?.label || id;
  const showEvidence = (id) =>
    setEvidence(data.evidence.find((e) => e.id === id));
  return (
    <div className="om-shell">
      <main className="om-main">
        {error ? (
          <div role="alert" className="om-empty">
            <AlertCircle />
            <h2>Memory is unavailable</h2>
            <p>{error}</p>
            <button
              className="om-button"
              onClick={() => setRetry((x) => x + 1)}
            >
              Try again
            </button>
          </div>
        ) : !data ? (
          <div className="om-empty" role="status">
            Opening operational memory…
          </div>
        ) : recordId ? (
          <>
            <div className="om-breadcrumb">
              <Link to="/operational-memory">Operational Memory</Link>
              <span>/</span>
              {recordId}
            </div>
            {!record ? (
              <div className="om-empty">
                <h1>Record not found</h1>
                <p>This record is not in the loaded collection.</p>
              </div>
            ) : (
              <>
                <div className="om-detail-heading">
                  <div>
                    <div className="om-eyebrow">
                      INCIDENT MEMORY · {record.id}
                    </div>
                    <h1>{name(record.id)}</h1>
                    <p>{record.summary}</p>
                  </div>
                  <Tag
                    kind={
                      record.availability === "available" ? "green" : "purple"
                    }
                  >
                    {record.availability === "available"
                      ? "Original source available"
                      : "Referenced · unavailable"}
                  </Tag>
                </div>
                {record.availability === "available" ? (
                  <div className="om-story-grid">
                    <section>
                      <div className="om-section-heading">
                        <div>
                          <span className="om-eyebrow">
                            RECONSTRUCTED OPERATIONAL STORY
                          </span>
                          <h2>What happened. What was decided. Why.</h2>
                        </div>
                        <Tag>Source sequence</Tag>
                      </div>
                      <p className="om-muted">
                        Reconstructed from document sections. Dates appear only
                        where verified; this is not a complete event chronology.
                      </p>
                      <div className="om-timeline">
                        {data.claims
                          .filter((c) => c.record_id === recordId)
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((c) => (
                            <article className="om-story-card" key={c.id}>
                              <div className="om-claim-meta">
                                <Tag
                                  kind={
                                    c.kind === "source_fact" ? "green" : "amber"
                                  }
                                >
                                  {c.kind === "source_fact"
                                    ? "Source-backed fact"
                                    : "Fluxonix interpretation"}
                                </Tag>
                                {c.source_date && <span>{c.source_date}</span>}
                              </div>
                              <h3>{c.title}</h3>
                              <p>{c.body}</p>
                              {data.dispositions
                                .filter((d) => d.claim_id === c.id)
                                .map((d) => (
                                  <div className="om-decision" key={d.claim_id}>
                                    <ShieldCheck size={18} />
                                    <div>
                                      <strong>{d.outcome}</strong>
                                      <small>{d.scope}</small>
                                    </div>
                                  </div>
                                ))}
                              <EvidenceButton
                                id={c.evidence_id}
                                data={data}
                                onClick={showEvidence}
                              />
                            </article>
                          ))}
                      </div>
                    </section>
                    <aside className="om-side">
                      <SourceCard record={record} />
                      <Connections
                        data={data}
                        recordId={recordId}
                        name={name}
                        showEvidence={showEvidence}
                      />
                      <Gaps
                        data={data}
                        recordId={recordId}
                        showEvidence={showEvidence}
                      />
                      <People
                        data={data}
                        recordId={recordId}
                        name={name}
                        showEvidence={showEvidence}
                      />
                    </aside>
                  </div>
                ) : (
                  <section className="om-panel om-gap-large">
                    <AlertCircle size={30} />
                    <h2>The reference is known. Its contents are not.</h2>
                    <p>
                      No PDF, facts, decisions, or people have been invented for{" "}
                      {recordId}.
                    </p>
                    {data.gaps
                      .filter((g) => g.missing_record_id === recordId)
                      .map((g) => (
                        <div key={g.id}>
                          <p>{g.description}</p>
                          <Link
                            to={"/operational-memory/records/" + g.record_id}
                          >
                            Back to {g.record_id} <ArrowRight size={14} />
                          </Link>
                          <EvidenceButton
                            id={g.evidence_id}
                            data={data}
                            onClick={showEvidence}
                          />
                        </div>
                      ))}
                  </section>
                )}
              </>
            )}
          </>
        ) : isAsk ? (
          <Ask
            data={data}
            query={query}
            goAsk={goAsk}
            showEvidence={showEvidence}
            name={name}
          />
        ) : (
          <>
            <section className="om-hero om-memory-hero">
              <span className="om-eyebrow">PUBLIC AEROSPACE DATASET · NASA FOIA</span>
              <h1>NASA released the records.<br />They did not release the memory.</h1>
              <p>Eight separate documents preserve fragments of an operational past: findings, decisions, references, and gaps. Fluxonix puts the fragments back into a path a team can actually follow.</p>
              <div className="om-hero-actions">
                <button className="om-button primary" onClick={() => document.getElementById("reconstruction")?.scrollIntoView({ behavior: "smooth" })}>Start the reconstruction <ArrowDown size={16} /></button>
                <button className="om-button" onClick={() => goAsk("Why were these records accepted Use As Is?")}>Ask the first question <ArrowRight size={16} /></button>
              </div>
              <div className="om-proof"><ShieldCheck size={14} /> {data.records.filter((r) => r.availability === "available").length} original NASA records · Every statement traces to source evidence</div>
            </section>
            <MemoryJourney data={data} goAsk={goAsk} name={name} />
            <div className="om-section-heading om-corpus-heading" id="records">
              <div><span className="om-eyebrow">THE SOURCE MATERIAL</span><h2>All the documents, once you know where to start</h2></div>
              <button className="om-button subtle" onClick={() => setUpload(true)}><Upload size={15} /> Add historical records</button>
            </div>
            <div className="om-record-grid">
              {data.records.filter((r) => r.availability === "available").map((r) => (
                <Link className="om-record-card" key={r.id} to={"/operational-memory/records/" + r.id}>
                  <div className="om-card-top"><span><FileText size={16} />{r.id}</span><ArrowUpRight size={19} /></div>
                  <h2>{name(r.id)}</h2><p>{r.summary}</p>
                  <div className="om-card-tags"><Tag kind="green">Source-backed</Tag><Tag>{r.page_count} source pages</Tag></div>
                  <footer>Reconstruct the operational story <ArrowRight size={16} /></footer>
                </Link>
              ))}
            </div>
            <section className="om-manifesto">
              <BookOpen size={27} />
              <h2>
                Recover yesterday’s operational knowledge.
                <br />
                Capture today’s. Connect both so tomorrow’s decisions are
                better.
              </h2>
              <Link to="/secure/my-incidents">
                Open today’s incident workspace <ArrowUpRight size={15} />
              </Link>
            </section>
          </>
        )}
      </main>
      <footer className="om-footer">
        FLUXONIX <span>Operational Memory · Source fidelity first</span>
        <span>Public NASA FOIA collection · MVP</span>
      </footer>
      {evidence && (
        <EvidencePanel
          evidence={evidence}
          data={data}
          close={() => setEvidence(null)}
        />
      )}
      {upload && <UploadPanel close={() => setUpload(false)} />}
    </div>
  );
}
function MemoryJourney({ data, goAsk }) {
  const available = data.records.filter((record) => record.availability === "available");
  const sourcePages = available.reduce((total, record) => total + (record.page_count || 0), 0);
  const missing = data.records.filter((record) => record.availability !== "available");

  return (
    <section className="om-memory-journey" id="reconstruction">
      <div className="om-story-intro">
        <span className="om-eyebrow">CHAPTER 1 · THE ARCHIVE</span>
        <h2>What NASA shared is useful but it arrives disconnected.</h2>
        <p>The release contains {available.length} records across {sourcePages} pages. Some describe a weld concern. Some describe a fairing. Six describe closely related rivet conditions. Each is a document; none is the complete operational story.</p>
      </div>
      <div className="om-fragment-grid">
        <article><FileText size={21} /><strong>{available.length} released records</strong><span>Original documents, not a prepared narrative</span></article>
        <article><Network size={21} /><strong>{data.relationships.length} visible connections</strong><span>References and similarities made inspectable</span></article>
        <article><LockKeyhole size={21} /><strong>{missing.length} known missing records</strong><span>Cause and corrective action remain unknown</span></article>
      </div>
      <div className="om-story-intro om-chapter-two">
        <span className="om-eyebrow">CHAPTER 2 · RECONSTRUCT ONE THREAD</span>
        <h2>Start with a condition. Follow it to the decision. Keep the uncertainty.</h2>
        <p>One record, N023825, shows the method. It does not become a precedent. It becomes evidence with its own boundaries.</p>
      </div>
      <div className="om-reconstruction-path">
        <article className="om-path-step"><span>01</span><FileSearch size={20} /><h3>A concern enters the record</h3><p>A letter reports that some strut welds may not meet design requirements and could have escaped inspection.</p><button className="om-evidence-link" onClick={() => goAsk("What did the weld inspection find?")}>Read the finding <ArrowRight size={13} /></button></article>
        <ArrowRight className="om-path-arrow" size={19} />
        <article className="om-path-step"><span>02</span><ShieldCheck size={20} /><h3>Evidence explains a decision</h3><p>Inspection, analysis assumptions, and an operations directive explain why a Use As Is disposition was recorded.</p><Link className="om-evidence-link" to="/operational-memory/records/N023825">Open the reconstructed story <ArrowRight size={13} /></Link></article>
        <ArrowRight className="om-path-arrow" size={19} />
        <article className="om-path-step gap-step"><span>03</span><AlertCircle size={20} /><h3>The record stops before cause</h3><p>Cause and corrective action are deferred to N023824. NASA referenced it; this collection does not contain it.</p><button className="om-evidence-link" onClick={() => goAsk("What cause records are missing?")}>Inspect the gap <ArrowRight size={13} /></button></article>
      </div>
      <div className="om-story-close">
        <div><span className="om-eyebrow">CHAPTER 3 · MEMORY, NOT JUST DOCUMENTS</span><h2>Now ask what the archive can support.</h2><p>Ask a question, open the cited passage, and inspect the original NASA page. Fluxonix shows the path and where the path ends.</p></div>
        <QuestionBox goAsk={goAsk} />
      </div>
    </section>
  );
}

function QuestionBox({ goAsk, initial = "" }) {
  const [q, setQ] = useState(initial);
  useEffect(() => setQ(initial), [initial]);
  return (
    <form
      className="om-question"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) goAsk(q.trim());
      }}
    >
      <Search size={20} />
      <input
        aria-label="Ask Operational Memory"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Ask about an incident, a decision, or what happened before…"
        maxLength={1500}
      />
      <button className="om-button primary" disabled={!q.trim()}>
        Ask memory <ArrowRight size={17} />
      </button>
    </form>
  );
}
function EvidenceButton({ id, data, onClick }) {
  const e = data.evidence.find((x) => x.id === id);
  return e ? (
    <button className="om-evidence-link" onClick={() => onClick(id)}>
      <FileText size={14} />
      {e.record_id} · PDF page {e.page}
      <ArrowUpRight size={13} />
    </button>
  ) : null;
}
function SourceCard({ record }) {
  return (
    <section className="om-panel">
      <span className="om-eyebrow">ORIGINAL DOCUMENT</span>
      <h3>
        <FileText size={18} /> {record.id}
      </h3>
      <p>{record.page_count} pages · NASA FOIA archive</p>
      <a
        className="om-button"
        href={`${API}/documents/${record.id}`}
        target="_blank"
        rel="noreferrer"
      >
        Open original PDF <ArrowUpRight size={15} />
      </a>
      <details className="om-source-details">
        <summary>Document provenance</summary>
        <p>Local original verified by SHA-256</p>
        <code>{record.sha256}</code>
        <a href={record.source_url} target="_blank" rel="noreferrer">
          NASA source <ArrowUpRight size={12} />
        </a>
      </details>
    </section>
  );
}
function Connections({ data, recordId, name, showEvidence }) {
  const edges = data.relationships.filter(
    (r) =>
      (r.from_id === recordId || r.to_id === recordId) &&
      !["PARTICIPATED_IN", "AUTHORED"].includes(r.relation),
  );
  return (
    <section className="om-panel">
      <span className="om-eyebrow">CONNECTED MEMORY</span>
      <h3>Relationships you can inspect</h3>
      {edges.map((r) => {
        const target = r.from_id === recordId ? r.to_id : r.from_id;
        return (
          <div className="om-connection" key={r.id}>
            <Tag kind={r.basis === "inferred" ? "amber" : "green"}>
              {r.basis === "inferred"
                ? "Inferred similarity"
                : r.relation.replaceAll("_", " ")}
            </Tag>
            {data.records.some((n) => n.id === target) ? (
              <Link to={"/operational-memory/records/" + target}>
                {target} · {name(target)}
              </Link>
            ) : (
              <strong>{name(target)}</strong>
            )}
            <p>{r.rationale}</p>
            {[
              ...new Set([
                r.evidence_id,
                ...data.relationship_support
                  .filter((s) => s.relationship_id === r.id)
                  .map((s) => s.evidence_id),
              ]),
            ].map((id) => (
              <EvidenceButton
                key={id}
                id={id}
                data={data}
                onClick={showEvidence}
              />
            ))}
          </div>
        );
      })}
    </section>
  );
}
function Gaps({ data, recordId, showEvidence }) {
  return (
    <section className="om-panel purple-top">
      <span className="om-eyebrow purple">EXPLICIT KNOWLEDGE GAPS</span>
      {data.gaps
        .filter((g) => !recordId || g.record_id === recordId)
        .map((g) => (
          <div className="om-connection" key={g.id}>
            {g.missing_record_id && (
              <Link to={"/operational-memory/records/" + g.missing_record_id}>
                {g.missing_record_id} · Unavailable
              </Link>
            )}
            <p>{g.description}</p>
            <EvidenceButton
              id={g.evidence_id}
              data={data}
              onClick={showEvidence}
            />
          </div>
        ))}
    </section>
  );
}
function People({ data, recordId, name, showEvidence }) {
  return (
    <section className="om-panel">
      <span className="om-eyebrow">PEOPLE & WORK PARTICIPATION</span>
      <p className="om-muted">
        Names are redacted. Each entry represents one visible source role, not
        an identified person.
      </p>
      {data.relationships
        .filter(
          (r) =>
            r.to_id === recordId &&
            ["PARTICIPATED_IN", "AUTHORED"].includes(r.relation),
        )
        .map((r) => (
          <div className="om-person" key={r.id}>
            <Users size={18} />
            <div>
              <strong>{name(r.from_id)}</strong>
              <small>{r.relation.replaceAll("_", " ")}</small>
              <EvidenceButton
                id={r.evidence_id}
                data={data}
                onClick={showEvidence}
              />
            </div>
          </div>
        ))}
    </section>
  );
}
function Ask({ data, query, goAsk, showEvidence, name }) {
  const [result, setResult] = useState(null),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setResult(null);
    setError("");
    if (!query.trim()) return;
    request("/ask", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question: query }),
    })
      .then((d) => active && setResult(d))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [query]);
  return (
    <>
      <div className="om-breadcrumb">
        <Link to="/operational-memory">Operational Memory</Link>
        <span>/</span>Ask memory
      </div>
      <h1 className="om-ask-title">Ask. Understand. Verify.</h1>
      <QuestionBox goAsk={goAsk} initial={query} />
      <div className="om-story-grid">
        <section className="om-answer">
          <div className="om-section-heading">
            <h2>{query}</h2>
            <Tag kind="green">Evidence first</Tag>
          </div>
          {!query ? (
            <div>
              <p>Start with a question about the loaded records.</p>
              <div className="om-suggestions">
                {suggested.map((q) => (
                  <button key={q} onClick={() => goAsk(q)}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : error ? (
            <p role="alert">{error}</p>
          ) : !result ? (
            <p role="status">Finding source passages…</p>
          ) : (
            <>
              <p className="om-muted">{result.method}</p>
              <p>{result.message}</p>
              {result.claims.map((c) => (
                <article key={c.id} className="om-answer-claim">
                  <Tag kind="green">Source-backed fact</Tag>
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                  <EvidenceButton
                    id={c.evidence_id}
                    data={data}
                    onClick={showEvidence}
                  />
                  <Link
                    className="om-story-link"
                    to={"/operational-memory/records/" + c.record_id}
                  >
                    Open reconstructed story <ArrowRight size={13} />
                  </Link>
                </article>
              ))}
              {result.participation.length > 0 && (
                <>
                  <p>
                    Only operational roles are visible. Personal identities are
                    redacted in the original documents.
                  </p>
                  {result.participation.map((r) => (
                    <div className="om-connection" key={r.id}>
                      <strong>{name(r.from_id)}</strong>
                      <p>{r.rationale}</p>
                      <EvidenceButton
                        id={r.evidence_id}
                        data={data}
                        onClick={showEvidence}
                      />
                    </div>
                  ))}
                </>
              )}
              {result.status === "not_supported" && (
                <div className="om-suggestions">
                  {suggested.map((q) => (
                    <button key={q} onClick={() => goAsk(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div className="om-answer-note">
                <AlertCircle size={18} />
                <span>
                  These passages describe historical records. Similar
                  dispositions do not establish the same cause, interchangeable
                  engineering rationale, or approval for other hardware.
                </span>
              </div>
            </>
          )}
        </section>
        <aside className="om-side">
          <section className="om-panel">
            <span className="om-eyebrow">HOW FLUXONIX KNOWS</span>
            <h3>Follow the evidence.</h3>
            <ol className="om-steps">
              <li>Read a source-backed statement</li>
              <li>Open its evidence passage</li>
              <li>Inspect the scanned NASA page</li>
              <li>Open the complete original PDF</li>
            </ol>
          </section>
          {result && (
            <Gaps
              data={{ ...data, gaps: result.gaps }}
              showEvidence={showEvidence}
            />
          )}
        </aside>
      </div>
    </>
  );
}
function Modal({ close, children, label, wide = false }) {
  const ref = useRef();
  useEffect(() => {
    const prior = document.activeElement;
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    function key(e) {
      if (e.key === "Escape") close();
      if (e.key === "Tab") {
        const all = ref.current.querySelectorAll(
          'button,a,input,summary,[tabindex="0"]',
        );
        const first = all[0],
          last = all[all.length - 1];
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    }
    document.addEventListener("keydown", key);
    return () => {
      document.body.style.overflow = old;
      document.removeEventListener("keydown", key);
      prior?.focus();
    };
  }, [close]);
  return (
    <div
      className="om-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <section
        className={"om-modal " + (wide ? "wide" : "")}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={ref}
      >
        {children}
      </section>
    </div>
  );
}
function EvidencePanel({ evidence: e, data, close }) {
  const r = data.records.find((r) => r.id === e.record_id);
  const [page, setPage] = useState(e.page),
    [failed, setFailed] = useState(false);
  return (
    <Modal close={close} wide label="Source evidence">
      <header className="om-modal-head">
        <div>
          <span className="om-eyebrow">VERIFY THE SOURCE</span>
          <h2>{e.record_id} · Evidence</h2>
        </div>
        <button className="om-icon" onClick={close} aria-label="Close evidence">
          <X />
        </button>
      </header>
      <div className="om-evidence-layout">
        <aside>
          <Tag kind="green">Source evidence</Tag>
          <h3>{e.locator}</h3>
          <blockquote>{e.excerpt}</blockquote>
          <p className="om-muted">
            Manually transcribed excerpt; bracketed text marks redaction. Check
            the scan for the full context.
          </p>
          <button
            className="om-evidence-link"
            onClick={() => {
              setPage(e.page);
              setFailed(false);
            }}
          >
            Return to cited PDF page {e.page}
          </button>
          <a
            className="om-button primary"
            href={`${API}/documents/${r.id}#page=${e.page}`}
            target="_blank"
            rel="noreferrer"
          >
            Open original document <ArrowUpRight size={16} />
          </a>
          <a
            className="om-text-button"
            href={r.source_url + "#page=" + e.page}
            target="_blank"
            rel="noreferrer"
          >
            View NASA-hosted original <ArrowUpRight size={14} />
          </a>
          <details className="om-source-details">
            <summary>Verify document checksum</summary>
            <code>{r.sha256}</code>
          </details>
        </aside>
        <section className="om-scan">
          <div className="om-page-controls">
            <button
              className="om-icon"
              disabled={page === 1}
              aria-label="Previous source page"
              onClick={() => {
                setPage((p) => p - 1);
                setFailed(false);
              }}
            >
              <ChevronLeft />
            </button>
            <span>
              PDF page {page} of {r.page_count}
            </span>
            <button
              className="om-icon"
              disabled={page === r.page_count}
              aria-label="Next source page"
              onClick={() => {
                setPage((p) => p + 1);
                setFailed(false);
              }}
            >
              <ChevronRight />
            </button>
          </div>
          {failed ? (
            <p role="alert">
              Page preview unavailable. Use the original document link.
            </p>
          ) : (
            <img
              src={`${API}/documents/${r.id}/pages/${page}`}
              alt={`Original NASA ${r.id}, PDF page ${page}`}
              onError={() => setFailed(true)}
            />
          )}
        </section>
      </div>
    </Modal>
  );
}
function UploadPanel({ close }) {
  const [files, setFiles] = useState([]),
    [error, setError] = useState("");
  return (
    <Modal close={close} label="Add historical records">
      <header className="om-modal-head">
        <h2>Add historical records</h2>
        <button className="om-icon" aria-label="Close upload" onClick={close}>
          <X />
        </button>
      </header>
      <div className="om-upload-body">
        <Upload size={32} />
        <h3>Bring another piece of the story.</h3>
        <p>
          Select PDFs for a local review list. This MVP does not upload, ingest,
          or add their contents to answers. The list clears when this panel
          closes.
        </p>
        <input
          aria-label="Select historical PDF records"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          onChange={(e) => {
            const fs = Array.from(e.target.files);
            setError(
              fs.some((f) => !f.name.toLowerCase().endsWith(".pdf"))
                ? "Select PDF files only."
                : "",
            );
            setFiles(fs.filter((f) => f.name.toLowerCase().endsWith(".pdf")));
          }}
        />
        {error && <p role="alert">{error}</p>}
        {files.map((f, i) => (
          <div className="om-connection" key={i}>
            <FileText size={16} /> {f.name}
            <small>
              {Math.ceil(f.size / 1024)} KB · Selected locally · Not ingested
            </small>
          </div>
        ))}
      </div>
    </Modal>
  );
}
